import { useCallback, useEffect } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button, isLoggedIn, SpinnerCenter, TextField } from "@shapr3d/frontend-lib";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { FormWrapper } from "../components/FormWrapper/FormWrapper";
import Heading from "../components/Heading/Heading";
import { useLoginCredentials } from "../contexts/LoginCredentialsContext";
import useNavigation from "../hooks/navigation";
import { useLogin } from "../hooks/useAuth";
import { useLoginRedirect } from "../hooks/useLoginRedirect";
import useTranslation from "../hooks/useTranslation";
import { AuthError } from "../types";

import style from "./TwoFactorRecoveryPage.module.css";

const recoveryCodeSchema = z.object({
  recoveryCode: z.string().min(1, "Recovery code is required"),
});

type RecoveryFormData = z.infer<typeof recoveryCodeSchema>;

export const TwoFactorRecoveryPage = () => {
  const navigation = useNavigation();
  const { t, m } = useTranslation();
  const { credentials: loginCredentials, setCredentials } = useLoginCredentials();

  const { mutateAsync: mutateLogin, isPending: isLoginPending } = useLogin();

  const redirectCallback = useLoginRedirect();

  const {
    setError,
    formState: { errors, isSubmitting, isValid, isSubmitted },
    register,
    handleSubmit,
  } = useForm<RecoveryFormData>({
    resolver: zodResolver(recoveryCodeSchema),
    reValidateMode: "onSubmit",
    defaultValues: {
      recoveryCode: "",
    },
  });

  useEffect(() => {
    if (isLoggedIn()) {
      redirectCallback();
      return;
    }
  }, [redirectCallback]);

  useEffect(() => {
    if (isLoggedIn()) {
      return;
    }

    if (!loginCredentials) {
      navigation.goToSignIn();
    }
  }, [loginCredentials, navigation]);

  const onSubmit = useCallback(
    async (data: RecoveryFormData) => {
      if (!loginCredentials) {
        navigation.goToSignIn();
        return;
      }

      try {
        await mutateLogin(
          {
            email: loginCredentials.email,
            password: loginCredentials.password,
            twoFactorTotpRecoveryCode: data.recoveryCode,
          },
          {
            onSuccess: () => {
              setCredentials(null);
              redirectCallback();
            },
            onError: (error) => {
              if (error === AuthError.InvalidEmailPassword) {
                setError("recoveryCode", { message: "Invalid recovery code." });
              } else {
                setError("recoveryCode", { message: "Something went wrong. Please try again." });
              }
            },
          }
        );
      } catch (error) {
        // Error is handled by useEffect
      }
    },
    [loginCredentials, mutateLogin, redirectCallback, navigation, setCredentials, setError]
  );

  const isLoading = isLoginPending || isSubmitting;

  if (!loginCredentials) {
    return <SpinnerCenter />;
  }

  return (
    <FormWrapper>
      <form onSubmit={handleSubmit(onSubmit)} className={style.twoFactorRecovery} noValidate>
        <div className={style.header}>
          <Heading type="h1">{t(m.TwoFactorAuth.Reset2FA)}</Heading>
          <span>{t(m.TwoFactorAuth.EnterRecoveryCode)}</span>
        </div>
        <TextField
          autoFocus
          errorMessage={errors.recoveryCode?.message}
          aria-label="recovery code"
          type="text"
          placeholder="Recovery code"
          error={Boolean(errors.recoveryCode)}
          maxLength={50}
          {...register("recoveryCode")}
        />
        <Button
          type="submit"
          loading={isLoading}
          aria-label="continue"
          disabled={!isValid && isSubmitted}
        >
          {t(m.General.Continue)}
        </Button>
        <Button
          variant="grayscale"
          className={style.backButton}
          onClick={() => navigation.goToSignIn()}
        >
          {t(m.ResetPassword.BackToLogin)}
        </Button>
      </form>
    </FormWrapper>
  );
};
